import React, { useEffect } from "react";
import { Navigate, Link } from "react-router-dom";
import { CheckCircle, ArrowRight, MapPin, BadgeEuro } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import HowItWorks from "../components/HowItWorks";
import ResponsiveImage from "../components/ResponsiveImage";
import FAQ from "../components/FAQ";
import CalculatorWidget from "../components/CalculatorWidget";
import Contact from "../components/Contact";
import { images } from "../data/images";
import { getServiceBySlug } from "../data/services";
import { servicePrices, PRICE_NOTE } from "../data/pricing";
import { locations } from "../data/locations";

const SITE_URL = "https://www.clevencleaning.nl";

export default function ServicePage({ slug }) {
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) return <Navigate to="/" replace />;

  const price = servicePrices[slug];
  const url = `${SITE_URL}/${slug}`;
  const related = service.related.map(getServiceBySlug).filter(Boolean);
  const lower = service.title.toLowerCase();

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "@id": `${url}#service`,
        serviceType: service.title,
        name: `${service.title} Rosmalen & Den Bosch`,
        description: service.metaDescription,
        url,
        image: `${SITE_URL}${images[service.image].fallback}`,
        provider: {
          "@type": "LocalBusiness",
          name: "Cleven Cleaning",
          telephone: "+31683653511",
          url: SITE_URL,
          address: { "@type": "PostalAddress", addressLocality: "Rosmalen", addressRegion: "Noord-Brabant", addressCountry: "NL" },
          aggregateRating: { "@type": "AggregateRating", ratingValue: "4.8", reviewCount: "49", bestRating: "5" },
        },
        areaServed: locations.map((l) => ({ "@type": "City", name: l.name })),
        offers: {
          "@type": "Offer",
          priceCurrency: "EUR",
          price: price.from.replace("€", ""),
          description: `Vanaf ${price.from} ${price.unit}`,
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
          { "@type": "ListItem", position: 2, name: "Diensten", item: `${SITE_URL}/#diensten` },
          { "@type": "ListItem", position: 3, name: service.title, item: url },
        ],
      },
    ],
  };

  return (
    <>
      <SEO title={service.metaTitle} description={service.metaDescription} canonicalUrl={url} jsonLd={jsonLd} />

      <PageHero
        testId="service-hero"
        crumbs={[{ label: "Home", to: "/" }, { label: "Diensten", href: "/#diensten" }, { label: service.title }]}
        badge={service.category === "hogedruk" ? "Hogedrukreiniging" : "Schoonmaakdienst"}
        title={service.h1}
        intro={service.intro}
        extra={
          <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3 mb-8" data-testid="service-price-chip">
            <BadgeEuro className="w-6 h-6 text-teal-300" />
            <div>
              <p className="text-white font-bold leading-tight">Vanaf {price.from} {price.unit}</p>
              <p className="text-white/60 text-xs">{price.note}</p>
            </div>
          </div>
        }
        aside={
          <div className="relative rounded-3xl overflow-hidden shadow-2xl aspect-[4/5] lg:aspect-[3/4]">
            <ResponsiveImage
              image={images[service.image]}
              alt={`${service.title} door Cleven Cleaning in Rosmalen en Den Bosch`}
              sizes="(min-width: 1024px) 40vw, 100vw"
              loading="eager"
              fetchPriority="high"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        }
      />

      {/* Body + benefits */}
      <section className="py-24 bg-white" aria-labelledby="service-body-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <div>
            <h2 id="service-body-heading" className="text-3xl sm:text-4xl font-bold text-slate-800 mb-6">
              Waarom {lower} door Cleven Cleaning?
            </h2>
            <div className="space-y-5 text-lg text-slate-700 leading-relaxed" data-testid="service-body">
              {service.body.map((p) => <p key={p.slice(0, 30)}>{p}</p>)}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {service.benefits.map((b) => (
              <div key={b.title} className="bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-6 border border-slate-100">
                <CheckCircle className="w-7 h-7 text-teal-500 mb-3" />
                <h3 className="font-bold text-slate-800 mb-1">{b.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HowItWorks />

      {/* Prijs */}
      <section className="py-20 bg-white" aria-labelledby="service-price-heading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-sky-900 to-teal-900 p-8 sm:p-12 text-white text-center shadow-2xl">
            <h2 id="service-price-heading" className="text-3xl sm:text-4xl font-bold mb-3">
              Wat kost {lower}?
            </h2>
            <p className="text-5xl sm:text-6xl font-bold bg-gradient-to-r from-sky-300 to-teal-300 bg-clip-text text-transparent mb-2">
              vanaf {price.from}
            </p>
            <p className="text-white/70 mb-6">{price.unit} &middot; {price.note}</p>
            <p className="text-sm text-white/60 max-w-xl mx-auto mb-8">{PRICE_NOTE}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="#calculator" className="inline-flex items-center justify-center px-8 py-4 bg-white text-sky-700 font-semibold rounded-xl hover:bg-sky-50 transition-colors shadow-lg" data-testid="service-price-calculator-btn">
                Bereken uw exacte prijs
              </a>
              <Link to="/kosten-glazenwasser" className="inline-flex items-center justify-center px-8 py-4 bg-white/15 text-white font-semibold rounded-xl hover:bg-white/25 border border-white/20 transition-colors" data-testid="service-price-overview-link">
                Bekijk alle tarieven
              </Link>
            </div>
          </div>
        </div>
      </section>

      <FAQ items={service.faq} title={`Veelgestelde vragen over ${lower}`} badge="Vragen & antwoorden" />

      {/* Locaties */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-sky-50" aria-labelledby="service-locations-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 id="service-locations-heading" className="text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
            {service.title} in uw plaats
          </h2>
          <p className="text-slate-600 mb-8">Wij verzorgen {lower} in de hele regio 's-Hertogenbosch, zonder voorrijkosten.</p>
          <div className="flex flex-wrap justify-center gap-3">
            {locations.map((loc) => (
              <Link
                key={loc.slug}
                to={`/glazenwasser-${loc.slug}`}
                data-testid={`service-location-link-${loc.slug}`}
                className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 text-slate-700 shadow-sm border border-slate-100 hover:border-sky-300 hover:text-sky-600 transition-colors"
              >
                <MapPin className="w-4 h-4 text-teal-500" />
                {loc.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Gerelateerde diensten */}
      <section className="py-20 bg-white" aria-labelledby="related-heading">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="related-heading" className="text-2xl sm:text-3xl font-bold text-slate-800 mb-8 text-center">
            Vaak gecombineerd met {lower}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`related-service-link-${r.slug}`}
                className="group flex items-center justify-between gap-3 bg-gradient-to-br from-slate-50 to-sky-50 rounded-2xl p-5 border border-slate-100 hover:border-sky-300 hover:shadow-lg transition-all"
              >
                <span className="font-semibold text-slate-800 group-hover:text-sky-600 transition-colors">{r.title}</span>
                <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-sky-500 group-hover:translate-x-1 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CalculatorWidget />
      <Contact />
    </>
  );
}
